import {createContext, useContext, useMemo} from 'react';

import {envIsPublicCloud} from '../../config';
import {useOrganizationFlagsQuery} from '../../generated/graphql';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {jsonParseSafe} from '../../util/json';
import {useStatsigGateWorkspaceAgent} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {useNavContext} from '../NavContextProvider';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';

/**
 * useAgentChatAvailability provides availability information on the chat agent
 * feature. This is intended to be called in components that need to check
 * whether the feature is enabled.
 */
export function useAgentChatAvailability(): AgentChatAvailability {
  return useContext(AgentChatAvailabilityContext);
}

export const AgentChatAvailabilityContext =
  createContext<AgentChatAvailability>({
    isAvailable: false,
    unavailableReason: 'loading',
  });

export interface AgentChatAvailability {
  isAvailable: boolean;
  unavailableReason?: UnavailableReason;
}

export type UnavailableReason =
  | 'feature-flag-disabled'
  | 'org-setting-disabled'
  | 'no-entity-context'
  | 'no-project-context'
  | 'loading';

/**
 * useComputeAgentChatAvailability calculates the agent chat feature
 * availability by checking various feature gates.
 *
 * NOTE: This function is for use by the agent chat provider component; external
 * components should use `useAgentChatAvailability` instead.
 */
export function useComputeAgentChatAvailability(): AgentChatAvailability {
  const isFeatureGateEnabled = useStatsigGateWorkspaceAgent();

  const {params} = useNavContext();
  const entityName = params.entityName;
  const projectName = params.projectName;

  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = envIsPublicCloud
    ? selectedAccount?.name
    : DEFAULT_ONPREM_ORGANIZATION_NAME;
  const {enabled: areLlmFeaturesEnabled, loading: isLoading} =
    useLLMFeaturesEnabled(isFeatureGateEnabled ? orgName : undefined);

  return useMemo(
    () =>
      computeAvailability({
        areLlmFeaturesEnabled,
        entityName,
        projectName,
        isFeatureGateEnabled,
        isLoading,
        isPublicCloud: envIsPublicCloud,
        orgName,
      }),
    [
      areLlmFeaturesEnabled,
      entityName,
      projectName,
      isFeatureGateEnabled,
      isLoading,
      orgName,
    ]
  );
}

/** computeAvailability evaluates whether to enable the chat agent feature. */
export function computeAvailability(inputs: {
  areLlmFeaturesEnabled: boolean;
  entityName?: string;
  projectName?: string;
  isFeatureGateEnabled?: boolean;
  isLoading: boolean;
  isPublicCloud: boolean;
  orgName?: string;
}): AgentChatAvailability {
  if (!inputs.isFeatureGateEnabled) {
    return {isAvailable: false, unavailableReason: 'feature-flag-disabled'};
  }

  if (!inputs.entityName) {
    return {isAvailable: false, unavailableReason: 'no-entity-context'};
  }

  if (!inputs.projectName) {
    return {isAvailable: false, unavailableReason: 'no-project-context'};
  }

  if (inputs.isLoading || (inputs.isPublicCloud && !inputs.orgName)) {
    return {isAvailable: false, unavailableReason: 'loading'};
  }

  if (!inputs.areLlmFeaturesEnabled) {
    return {isAvailable: false, unavailableReason: 'org-setting-disabled'};
  }

  return {isAvailable: true};
}

/**
 * useLLMFeaturesEnabled queries organization flags and extracts the current
 * value for the `enable_llm_powered_features` setting.
 */
function useLLMFeaturesEnabled(orgName?: string): {
  enabled: boolean;
  loading: boolean;
} {
  const {data, loading} = useOrganizationFlagsQuery({
    variables: {orgName: orgName ?? ''},
    skip: !orgName,
    fetchPolicy: 'cache-and-network',
  });

  if (!orgName) {
    return {enabled: false, loading: false};
  }

  if (loading && !data) {
    return {enabled: false, loading: true};
  }

  const flags =
    data?.organization?.flags != null
      ? jsonParseSafe(data.organization.flags)
      : {};

  return {
    enabled: flags?.enable_llm_powered_features ?? false,
    loading: false,
  };
}
